/**
 * English copy for the notifications feature (device permission, realtime
 * status, generic "private" fallbacks, and the settings page).
 *
 * Syntry has no i18n library (see CLAUDE.md — keep the lean stack); this is a
 * flat, typed key → string map read through `notification-copy.ts`. Keys follow
 * the master plan's LANG catalogue (`notifications.device.*` LANG-025..032 +
 * 071/072, `notifications.realtime.*` LANG-033..036, `notifications.private.*`
 * LANG-073/074); the `notifications.settings.*` keys are the page-local extras
 * this screen needs. Only `en` is selectable today, so this is the sole locale.
 */
export const notificationsEn = {
  // ── Device (host) notifications ────────────────────────────────────────
  'notifications.device.enable': 'Enable device notifications',
  'notifications.device.disable': 'Disable device notifications',
  'notifications.device.rationale':
    'Get a heads-up on your device when something needs you — approvals, messages and company advisories — even while Syntry is in the background. Private details stay inside the app; the alert only tells you there is something to open.',
  'notifications.device.enabled': 'Device notifications are on for this device.',
  'notifications.device.disabled': 'Device notifications are off. Turn them on to be alerted outside the app.',
  'notifications.device.default': 'Not enabled yet. Tap the button and allow notifications when your device asks.',
  'notifications.device.denied':
    'Notifications are blocked for Syntry. Allow them in your device or browser settings, then come back here.',
  'notifications.device.unsupported':
    'Device notifications are not available here yet. Use the Syntry web app in a modern browser over a secure connection, or the in-app inbox.',
  'notifications.device.invalid':
    'This device is no longer registered for notifications. Enable them again to reconnect.',
  'notifications.device.openSettings': 'Open your device settings to change notification access for Syntry.',
  // ── Phase 6: the five-way model (phase6-contract.md §3.5.2) ───────────
  // Background delivery (the endpoint + tenant-policy axes), shown under the
  // state line while device notifications are on.
  'notifications.device.backgroundOn': 'Background delivery is on: alerts reach this device even when Syntry is closed.',
  'notifications.device.backgroundPending': 'Registering this device for background delivery…',
  'notifications.device.backgroundUnavailable':
    'Background delivery is not available on this deployment yet. Alerts arrive while Syntry is open.',
  'notifications.device.backgroundInvalid':
    'Background delivery could not be set up for this device. Tap Enable again to retry.',
  // The server-side master switch (the General toggle above) is off.
  'notifications.device.serverSwitchOff':
    'Notifications are switched off for your account. Turn on the Notifications switch above first.',
  'notifications.device.serverSwitchOffToast': 'Turn on the Notifications switch first.',
  'notifications.device.enabledToast': 'Device notifications are on.',
  'notifications.device.disabledToast': 'Device notifications are off.',
  'notifications.device.deniedToast': 'Notifications are blocked. Allow them in your device settings.',
  'notifications.device.unavailableToast': 'Device notifications are not available here.',

  // ── Realtime connection ───────────────────────────────────────────────
  'notifications.realtime.connected': 'Live updates connected',
  'notifications.realtime.reconnecting': 'Reconnecting to live updates…',
  'notifications.realtime.offline': 'Live updates offline — checking every minute',
  'notifications.realtime.syncing': 'Syncing…',
  // Reason lines shown under the status while it is not "connected" (see
  // `realtimeErrorCopy`). Content-free: they name a stage, never server text.
  'notifications.realtime.errorOrigin':
    'The live-updates server rejected this app’s address. Open Syntry from its configured address.',
  'notifications.realtime.errorBootstrap': 'Could not reach the server to start live updates — retrying.',
  'notifications.realtime.errorConnection': 'The live-updates connection dropped — reconnecting.',
  'notifications.realtime.errorAuth': 'Live updates could not be authorized for this account.',
  'notifications.realtime.errorRefused':
    'The live-updates server closed this connection — reconnecting with a fresh session.',
  // The two DEC-102 gateway guards (realtime.service.ts). Loopback is the hard
  // one — the app does not dial and polls instead; "unexpected" is advisory
  // and the socket is still tried, so the line only shows until it connects.
  'notifications.realtime.errorGatewayLoopback':
    'The live-updates server address points at this device instead of a server. Live updates are paused; the inbox still refreshes on its own.',
  'notifications.realtime.errorGatewayUnexpected':
    'The live-updates server your company points to is not the one this app expects. Trying anyway — if it does not connect, ask your administrator to check the server address.',
  // Catch-up (cursor) state shown beside the connection status.
  'notifications.realtime.syncIdle': 'Waiting for the first sync.',
  'notifications.realtime.synchronized': 'Up to date',
  'notifications.realtime.degraded': 'Some updates may be delayed — catching up when the server is reachable.',

  // ── Generic (privacy-safe) fallbacks ──────────────────────────────────
  'notifications.private.title': 'New update',
  'notifications.private.body': 'Open Syntry to view a private update.',
  // Message events are ALWAYS content-free on the wire (privacy policy): the
  // alert says a message arrived, never who from or what it says.
  'notifications.message.title': 'New message',
  'notifications.message.body': 'Open Messages to read it.',
  'notifications.message.toastAction': 'Open',
  // A burst of several notifications in one coalesce window gets ONE toast.
  'notifications.burst.title': 'New notifications',
  'notifications.burst.body': '{count} new updates arrived.',

  // ── Settings page ─────────────────────────────────────────────────────
  'notifications.settings.title': 'Notifications',
  'notifications.settings.heroTitle': 'Stay in the loop',
  'notifications.settings.deviceSection': 'Device notifications',
  'notifications.settings.realtimeSection': 'Live updates',
  'notifications.settings.realtimeHint':
    'While Syntry is open, new notifications and messages arrive instantly over a private connection.',
  'notifications.settings.realtimeDisabled': 'Live updates are not enabled for your company yet. The inbox still refreshes normally.',
  'notifications.settings.realtimeConnecting': 'Connecting to live updates…',
  'notifications.settings.realtimeDisconnected': 'Live updates are paused.',
  'notifications.settings.statusLabel': 'Status',
  'notifications.settings.syncLabel': 'Inbox sync',
  'notifications.settings.deviceWebDesc':
    'Get an alert on this device when something new arrives while Syntry is in the background.',
  'notifications.settings.deviceNativeDesc':
    'Reminders and live alerts appear here even when Syntry is in the background.',
  'notifications.settings.deviceRowWeb': 'This browser',
  'notifications.settings.deviceRowNative': 'This device',
  'notifications.settings.backgroundLabel': 'Background delivery',
  'notifications.settings.reminderPermissionTitle': 'Device reminders',
  'notifications.settings.reminderPermissionDesc':
    'Reminders can appear on this device only after notifications are allowed for Syntry.',
  'notifications.settings.reminderPermissionAllow': 'Allow device notifications',
  'notifications.settings.reminderPermissionDenied':
    'Notifications are blocked for Syntry. Turn them back on in your device settings to get reminders here.',
  'notifications.settings.reminderPermissionGranted': 'Device notifications are on.',
  'notifications.settings.reminderPermissionFailed': "Couldn't turn on device notifications.",
  'notifications.settings.permissionAllowed': 'Allowed',
  'notifications.settings.permissionNotAsked': 'Not asked',
  'notifications.settings.invalid': 'Needs attention',
  'notifications.settings.disabled': 'Off',
  'notifications.settings.working': 'Working…',
  'notifications.settings.testButton': 'Send me a test notification',
  'notifications.settings.testSending': 'Sending…',
  'notifications.settings.testSent': 'Test notification sent. It should appear in a moment.',
  'notifications.settings.testFailed': 'Could not send a test notification. Please try again.',
  'notifications.settings.testUnavailable': 'Connect to live updates first to send yourself a test.',
  'notifications.settings.requesting': 'Waiting for your answer…',
  'notifications.settings.granted': 'Enabled',
  'notifications.settings.denied': 'Blocked',
  'notifications.settings.undecided': 'Not enabled',
  'notifications.settings.unsupported': 'Unavailable',
  'notifications.settings.toastAction': 'Open',
} as const;

export type NotificationCopyKey = keyof typeof notificationsEn;
